import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ChevronRight, Anchor, Phone, MapPin, HelpCircle, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import BookingWidget from "@/components/BookingWidget";
import { PrimaryButton, SecondaryButton, SmartImage, Reveal, openEnquiry } from "@/components/common";
import { getPage, ALL_PAGES } from "@/data/landingPages";
import { IMAGES, CONTACT } from "@/data";

const SITE = "https://vividcruisessydney.com.au";

const LandingPage = () => {
  const { slug } = useParams();
  const page = getPage(slug);
  if (!page) return <Navigate to="/" replace />;

  const canonical = `${SITE}/${page.slug}`;
  const related = ALL_PAGES.filter((p) => p.slug !== page.slug).slice(0, 6);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: page.h1, item: canonical },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: page.h1,
      description: page.description,
      image: IMAGES.hero.src,
      brand: { "@type": "Brand", name: "Vivid Cruises Sydney" },
      offers: [
        { "@type": "Offer", name: "Adult Ticket", price: "49.00", priceCurrency: "AUD", availability: "https://schema.org/InStock", url: canonical },
        { "@type": "Offer", name: "Child Ticket", price: "35.00", priceCurrency: "AUD", availability: "https://schema.org/InStock", url: canonical },
      ],
      aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "687" },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <main className="bg-vivid-base pt-20">
      <Seo title={page.title} description={page.description} canonical={canonical} image={IMAGES.hero.src} jsonLd={jsonLd} />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="mx-auto max-w-7xl px-5 pt-6 sm:px-8">
        <ol className="flex items-center gap-2 text-sm text-white/50">
          <li><Link to="/" className="hover:text-white">Home</Link></li>
          <li><ChevronRight className="h-4 w-4" /></li>
          <li className="text-white/80">{page.h1}</li>
        </ol>
      </nav>

      {/* Header + booking on top */}
      <section className="mx-auto max-w-7xl px-5 pt-8 pb-6 sm:px-8">
        <div className="grid items-start gap-10 lg:grid-cols-[1fr_0.85fr]">
          <div>
            <h1 className="font-heading text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              <span className="text-gradient-vivid">{page.h1}</span>
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-white/80">{page.intro[0]}</p>
            <div className="mt-7 flex flex-wrap gap-4">
              <PrimaryButton onClick={() => document.querySelector("#tickets")?.scrollIntoView({ behavior: "smooth" })}>
                <Anchor className="h-5 w-5" /> Book Now
              </PrimaryButton>
              <SecondaryButton onClick={() => openEnquiry(page.h1)}>
                <Phone className="h-4 w-4" /> Enquire
              </SecondaryButton>
            </div>
            <p className="mt-5 inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/60">
              <MapPin className="h-4 w-4 text-vivid-magenta" /> Departs King Street Wharf, Wharf 3 · Adults $49 · Kids $35
              <a href={CONTACT.directions} target="_blank" rel="noopener noreferrer" data-testid="lp-directions-link" className="text-vivid-cyan underline-offset-2 hover:underline">Get directions</a>
            </p>
            <SmartImage image={IMAGES.hero} caption className="mt-8 h-64 rounded-3xl border border-white/10 sm:h-80" priority />
          </div>

          {/* Booking + enquiry at top */}
          <div id="tickets" className="scroll-mt-28 space-y-4">
            <BookingWidget prefix={`${page.slug}-`} />
            <button
              onClick={() => openEnquiry(page.h1)}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-vivid-cyan/30 bg-vivid-cyan/5 py-4 text-sm font-medium text-white transition hover:bg-vivid-cyan/10"
            >
              <Phone className="h-4 w-4 text-vivid-cyan" /> Prefer to ask first? Send an instant enquiry
            </button>
          </div>
        </div>
      </section>

      {/* Long-form content */}
      <article className="mx-auto max-w-4xl px-5 py-12 sm:px-8">
        <div className="space-y-4 text-white/75 leading-relaxed">
          {page.intro.slice(1).map((p, i) => (<p key={i}>{p}</p>))}
        </div>

        {page.sections.map((s, i) => (
          <Reveal key={i}>
            <section className="mt-12">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{s.h2}</h2>
              <div className="mt-4 space-y-4 text-white/75 leading-relaxed">
                {s.body.map((p, j) => (<p key={j}>{p}</p>))}
              </div>
            </section>
          </Reveal>
        ))}

        {/* FAQ */}
        <section className="mt-14">
          <h2 className="flex items-center gap-2 text-3xl font-bold tracking-tight sm:text-4xl">
            <HelpCircle className="h-7 w-7 text-vivid-cyan" /> Frequently asked questions
          </h2>
          <div className="mt-6 space-y-4">
            {page.faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-black/30 p-5">
                <h3 className="font-semibold text-white">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="mt-14 rounded-3xl border border-vivid-magenta/30 bg-gradient-to-br from-vivid-surface to-black p-8 text-center">
          <h2 className="text-2xl font-bold sm:text-3xl">Ready to book your {page.h1}?</h2>
          <p className="mx-auto mt-3 max-w-xl text-white/70">Secure your seats for Vivid Sydney 2027. Adults $49, kids $35, evening sailings sell out fast.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <PrimaryButton onClick={() => document.querySelector("#tickets")?.scrollIntoView({ behavior: "smooth" })}>Book Now</PrimaryButton>
            <SecondaryButton onClick={() => openEnquiry(page.h1)}>Request a Private Charter</SecondaryButton>
          </div>
        </div>
      </article>

      {/* Internal links */}
      <section className="mx-auto max-w-7xl px-5 pb-20 sm:px-8">
        <h2 className="text-2xl font-bold">Explore more Vivid cruises in Sydney</h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((r) => (
            <Link
              key={r.slug}
              to={`/${r.slug}`}
              className="group flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-white/80 transition hover:border-vivid-magenta/50 hover:text-white"
            >
              {r.h1}
              <ArrowRight className="h-4 w-4 opacity-0 transition group-hover:opacity-100" />
            </Link>
          ))}
        </div>
        <p className="mt-8 text-sm text-white/50">
          Questions about your cruise? Contact us at{" "}
          <a href={`mailto:${CONTACT.email}`} className="text-vivid-cyan hover:text-white">{CONTACT.email}</a>{" "}
          or visit the <Link to="/" className="text-vivid-cyan hover:text-white">Vivid Cruises Sydney home page</Link>.
        </p>
      </section>
    </main>
  );
};

export default LandingPage;
